import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Message } from 'primereact/message';
import { ProgressBar } from 'primereact/progressbar';
import { useAuth } from '../contexts/AuthContext';
import { useFormValidation } from '../hooks/useFormValidation';

const Register = () => {
  const navigate = useNavigate();
  const { register, isLoading, authError, clearAuthError } = useAuth();
  const { validateField, clearErrors, hasErrors, getFieldError, calculatePasswordStrength } = useFormValidation();
  const [formData, setFormData] = useState({
    firstName: '',
    surname: '',
    email: '',
    homePhone: '',
    workPhone: '',
    mobilePhone: '',
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    cityTown: '',
    county: '',
    postcode: '',
    gender: null,
    dateOfBirth: null,
    username: '',
    password: '',
    confirmPassword: '',
    howDidYouHear: '',
    gdprConsent: false
  });
  
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');

  const genderOptions = [
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' },
    { label: 'Other', value: 'other' },
    { label: 'Prefer not to say', value: 'not_specified' }
  ];

  const hearAboutUsOptions = [
    { label: 'Search Engine', value: 'search' },
    { label: 'Social Media', value: 'social' },
    { label: 'Friend/Family', value: 'referral' },
    { label: 'Event', value: 'event' },
    { label: 'Magazine/Publication', value: 'magazine' },
    { label: 'Other', value: 'other' }
  ];



  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (field === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearAuthError();
    setSuccessMessage('');
    
    clearErrors();
    
    // Validate form
    const isPasswordValid = validateField('password', formData.password, { 
      required: true, 
      minLength: 8, 
      passwordStrength: 50 
    });
    
    const isConfirmPasswordValid = validateField('confirmPassword', formData.confirmPassword, { 
      required: true, 
      match: formData.password 
    });
    
    if (!isPasswordValid || !isConfirmPasswordValid || hasErrors()) {
      return;
    }

    // Prepare member data for registration
    const memberData = {
      email: formData.email,
      password: formData.password,
      firstName: formData.firstName,
      lastName: formData.surname,
      phoneNumber: formData.homePhone || formData.workPhone,
      mobileNumber: formData.mobilePhone,
      memberTypeAlias: 'Member',
      address: {
        line1: formData.addressLine1,
        line2: formData.addressLine2,
        line3: formData.addressLine3,
        city: formData.cityTown,
        county: formData.county,
        postcode: formData.postcode,
        country: 'UK'
      }
    };

    const result = await register(memberData);
    
    if (result.success) {
      setSuccessMessage(result.message || 'Registration successful! Please check your email to verify your account.');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    }
  };

  const getPasswordStrengthClass = () => {
    if (passwordStrength < 50) return 'se-password-strength-weak';
    if (passwordStrength < 75) return 'se-password-strength-medium';
    return 'se-password-strength-strong';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength < 25) return 'Very Weak';
    if (passwordStrength < 50) return 'Weak';
    if (passwordStrength < 75) return 'Medium';
    return 'Strong';
  };

  return (
    <div className="se-auth-container">
      <div className="se-auth-card" style={{ maxWidth: '600px' }}>
        <div className="se-auth-header">
          <h1 className="se-auth-title">Create Account</h1>
          <p className="se-auth-subtitle">Join the Sport Endurance community</p>
        </div>

        <form onSubmit={handleSubmit}>
          {authError && (
            <Message
              severity="error"
              text={authError}
              className="w-full mb-3"
            />
          )}
          
          {successMessage && (
            <Message
              severity="success"
              text={successMessage}
              className="w-full mb-3"
            />
          )}

          {/* Personal Information */}
          <h3 className="se-text-primary mb-3">Personal Information</h3>
          
          <div className="grid">
            <div className="col-12 md:col-6">
              <div className="se-form-group">
                <label htmlFor="firstName" className="se-form-label">
                  First Name <span className="se-form-required">*</span>
                </label>
                <InputText
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className="w-full"
                  required
                />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="se-form-group">
                <label htmlFor="surname" className="se-form-label">
                  Surname <span className="se-form-required">*</span>
                </label>
                <InputText
                  id="surname"
                  value={formData.surname}
                  onChange={(e) => handleInputChange('surname', e.target.value)}
                  className="w-full"
                  required
                />
              </div>
            </div>
          </div>

          <div className="se-form-group">
            <label htmlFor="email" className="se-form-label">
              Email <span className="se-form-required">*</span>
            </label>
            <InputText
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="w-full"
              required
            />
          </div>

          {/* Contact Information */}
          <h3 className="se-text-primary mb-3 mt-4">Contact Information</h3>
          
          <div className="grid">
            <div className="col-12 md:col-4">
              <div className="se-form-group">
                <label htmlFor="homePhone" className="se-form-label">Home Phone</label>
                <InputText
                  id="homePhone"
                  value={formData.homePhone}
                  onChange={(e) => handleInputChange('homePhone', e.target.value)}
                  className="w-full"
                />
              </div>
            </div>
            <div className="col-12 md:col-4">
              <div className="se-form-group">
                <label htmlFor="workPhone" className="se-form-label">Work Phone</label>
                <InputText
                  id="workPhone"
                  value={formData.workPhone}
                  onChange={(e) => handleInputChange('workPhone', e.target.value)}
                  className="w-full"
                />
              </div>
            </div>
            <div className="col-12 md:col-4">
              <div className="se-form-group">
                <label htmlFor="mobilePhone" className="se-form-label">Mobile Phone</label>
                <InputText
                  id="mobilePhone"
                  value={formData.mobilePhone}
                  onChange={(e) => handleInputChange('mobilePhone', e.target.value)}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <h3 className="se-text-primary mb-3 mt-4">Address</h3>
          
          <div className="se-form-group">
            <label htmlFor="addressLine1" className="se-form-label">
              Address Line 1 <span className="se-form-required">*</span>
            </label>
            <InputText
              id="addressLine1"
              value={formData.addressLine1}
              onChange={(e) => handleInputChange('addressLine1', e.target.value)}
              className="w-full"
              required
            />
          </div>

          <div className="se-form-group">
            <label htmlFor="addressLine2" className="se-form-label">Address Line 2</label>
            <InputText
              id="addressLine2"
              value={formData.addressLine2}
              onChange={(e) => handleInputChange('addressLine2', e.target.value)}
              className="w-full"
            />
          </div>

          <div className="se-form-group">
            <label htmlFor="addressLine3" className="se-form-label">Address Line 3</label>
            <InputText
              id="addressLine3"
              value={formData.addressLine3}
              onChange={(e) => handleInputChange('addressLine3', e.target.value)}
              className="w-full"
            />
          </div>

          <div className="grid">
            <div className="col-12 md:col-4">
              <div className="se-form-group">
                <label htmlFor="cityTown" className="se-form-label">
                  City/Town <span className="se-form-required">*</span>
                </label>
                <InputText
                  id="cityTown"
                  value={formData.cityTown}
                  onChange={(e) => handleInputChange('cityTown', e.target.value)}
                  className="w-full"
                  required
                />
              </div>
            </div>
            <div className="col-12 md:col-4">
              <div className="se-form-group">
                <label htmlFor="county" className="se-form-label">County</label>
                <InputText
                  id="county"
                  value={formData.county}
                  onChange={(e) => handleInputChange('county', e.target.value)}
                  className="w-full"
                />
              </div>
            </div>
            <div className="col-12 md:col-4">
              <div className="se-form-group">
                <label htmlFor="postcode" className="se-form-label">
                  Postcode <span className="se-form-required">*</span>
                </label>
                <InputText
                  id="postcode"
                  value={formData.postcode}
                  onChange={(e) => handleInputChange('postcode', e.target.value)}
                  className="w-full"
                  required
                />
              </div>
            </div>
          </div>

          {/* Personal Details */}
          <h3 className="se-text-primary mb-3 mt-4">Personal Details</h3>
          
          <div className="grid">
            <div className="col-12 md:col-6">
              <div className="se-form-group">
                <label htmlFor="gender" className="se-form-label">Gender</label>
                <Dropdown
                  id="gender"
                  value={formData.gender}
                  options={genderOptions}
                  onChange={(e) => handleInputChange('gender', e.value)}
                  placeholder="Select gender"
                  className="w-full"
                />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="se-form-group">
                <label htmlFor="dateOfBirth" className="se-form-label">
                  Date of Birth <span className="se-form-required">*</span>
                </label>
                <Calendar
                  id="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.value)}
                  dateFormat="dd/mm/yy"
                  placeholder="Select date"
                  className="w-full"
                  maxDate={new Date()}
                  yearRange="1940:2010"
                  required
                />
              </div>
            </div>
          </div>

          {/* Account Details */}
          <h3 className="se-text-primary mb-3 mt-4">Account Details</h3>
          
          <div className="se-form-group">
            <label htmlFor="username" className="se-form-label">
              Username <span className="se-form-required">*</span>
            </label>
            <InputText
              id="username"
              value={formData.username}
              onChange={(e) => handleInputChange('username', e.target.value)}
              className="w-full"
              required
            />
          </div>

          <div className="se-form-group">
            <label htmlFor="password" className="se-form-label">
              Password <span className="se-form-required">*</span>
            </label>
            <Password
              id="password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              className="w-full"
              inputClassName="w-full"
              feedback={false}
              toggleMask
              required
            />
            {formData.password && (
              <div className={`se-password-strength ${getPasswordStrengthClass()}`}>
                <ProgressBar value={passwordStrength} className="mt-2" showValue={false} />
                <small className="block mt-1">
                  Password strength: {getPasswordStrengthText()}
                </small>
              </div>
            )}
          </div>

          <div className="se-form-group">
            <label htmlFor="confirmPassword" className="se-form-label">
              Please re-enter password <span className="se-form-required">*</span>
            </label>
            <Password
              id="confirmPassword"
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              className="w-full"
              inputClassName="w-full"
              feedback={false}
              toggleMask
              required
            />
          </div>

          {/* Additional Information */}
          <h3 className="se-text-primary mb-3 mt-4">Additional Information</h3>
          
          <div className="se-form-group">
            <label htmlFor="howDidYouHear" className="se-form-label">How did you hear about us?</label>
            <Dropdown
              id="howDidYouHear"
              value={formData.howDidYouHear}
              options={hearAboutUsOptions}
              onChange={(e) => handleInputChange('howDidYouHear', e.value)}
              placeholder="Please select"
              className="w-full"
            />
          </div>

          {/* GDPR Consent */}
          <div className="se-form-group mt-4">
            <div className="flex align-items-start">
              <Checkbox
                inputId="gdprConsent"
                checked={formData.gdprConsent}
                onChange={(e) => handleInputChange('gdprConsent', e.checked)}
                required
              />
              <label htmlFor="gdprConsent" className="ml-2 cursor-pointer">
                <span className="se-form-required">*</span> I agree to the processing of my personal data in accordance with the 
                <Link to="/privacy-policy" className="se-text-primary ml-1">Privacy Policy</Link>
                <br />
                <small className="text-600">
                  🔒 We use your information to manage your membership and communicate with you about events and services.
                </small>
              </label>
            </div>
          </div>

          <Button
            type="submit"
            label="Create Account"
            loading={isLoading}
            className="w-full p-button-primary mt-4 mb-3"
            size="large"
            disabled={!formData.gdprConsent}
          />

          <div className="text-center">
            <span className="text-600">Already have an account? </span>
            <Link to="/login" className="se-text-primary font-medium">
              Sign in here
            </Link>
          </div>
        </form>

        {/* Demo Message */}
        <div className="mt-4">
          <Message
            severity="info"
            text="This is a prototype. Registration will redirect to login page."
            className="w-full"
          />
        </div>
      </div>
    </div>
  );
};

export default Register;